import Portal from '@arcgis/core/portal/Portal';
import IdentityManager from '@arcgis/core/identity/IdentityManager';
import ServerInfo from '@arcgis/core/identity/ServerInfo';
import PortalQueryParams from '@arcgis/core/portal/PortalQueryParams';
import OAuthInfo from '@arcgis/core/identity/OAuthInfo';
import { useQuery } from '@tanstack/react-query';
import { PortalState } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import Sentry from '../../../../../instrument';
import type { ExternalLayerAccessType } from 'hooks/externalLayer/types';

const ARCGIS_PORTAL_URL = 'https://www.arcgis.com';
// Max allowed by the API is 100
const PAGE_SIZE = 100;
const drawableTypes = [
  'type:"Web Map"', // Map + optional layers
  'type:"Feature Service"', // hosted web service
  'type:"Feature Layer"', // individual collection of geographic features
  'type:"Map Service"', // Server-side rendered tiles (Map Image Layers)
  'type:"Vector Tile Service"', // Fast, high-res vector tiles
  'type:"Image Service"', // Raster/Imagery data
  'type:"Scene Service"', // 3D Objects and Meshes
  'type:"KML"', // Google Earth files
  'type:"WMS"', // Open-standard web map services
].join(' OR ');
const excludeDhsLayers = 'NOT tags:"dhs"';
export const ARCGIS_DISASTER_TECH_ORG_ID = '7db7BYtEpnk6FPo8';

export const oauthPortal = ({
  authMode,
  url,
}: {
  authMode: __esri.PortalProperties['authMode'];
  url?: string;
}) => {
  return new Portal({
    authMode,
    url: url ?? ARCGIS_PORTAL_URL,
  });
};

/**
 * Q: What is arcgis_portal_url for in the OAuth flow?
 *
 * A: It defines the authentication endpoint.
 *
 * - 1. MULTI-TENANT (Default): If set to 'https://www.arcgis.com', it allows any
 *        ArcGIS Online user (from any Org) to log in using our Client ID. This
 *        is the most flexible for multi-org setups.
 * - 2. BRANDED/ORGANIZATIONAL: If set to an Org URL (e.g.,
 *        'https://coastguard.maps.arcgis.com'), the login screen will show that
 *        Org's custom branding/logo.
 * - 3. ENTERPRISE (On-Prem): If a group uses a private ArcGIS Enterprise Portal,
 *        this MUST be set to their specific portal URL (e.g.,
 *        'https://gis.agency.gov/portal'), as the global 'www.arcgis.com'
 *        cannot see or authenticate against private servers.
 */
export function getPortalUrl(group: Group) {
  return group.arcgis_portal_url || ARCGIS_PORTAL_URL;
}

export function registerArcGisOAuthConfig(group: Group): string | null {
  const portalUrl = getPortalUrl(group);
  const meta: Record<string, unknown> = {
    portalUrl,
    groupGuid: group.group_guid,
    groupName: group.group_name,
    clientId: group.oauth_client_id,
  };

  if (!portalUrl || !group.oauth_client_id) {
    console.error(
      '[ArcGIS] registerArcGisOAuthConfig: missing portalUrl or oauth_client_id on group; cannot register ArcGIS OAuth config.',
      meta
    );
    return null;
  }

  try {
    // Register the server as a trusted domain and OAuth app config.
    const serverInfo = new ServerInfo({
      tokenServiceUrl: `${portalUrl}/sharing/rest/generateToken`, // Have to set this even though it's optional, otherwise TypeError
      server: portalUrl,
      hasPortal: true, // Explicitly tells the SDK this is a Portal/Enterprise instance
      hasServer: true, // Indicates it's an ArcGIS Server instance
      // webTierAuth: false, // Set to true only if using IWA/NTLM without OAuth
    });
    IdentityManager.registerServers([serverInfo]);

    const info = new OAuthInfo({
      appId: group.oauth_client_id,
      portalUrl,
      popup: false,
      preserveUrlHash: true,
      expiration: 129600, // 90 days
    });

    IdentityManager.registerOAuthInfos([info]);

    console.log(
      '[ArcGIS] registerArcGisOAuthConfig: registered ServerInfo + OAuthInfo with IdentityManager',
      meta
    );
    return portalUrl;
  } catch (error) {
    console.error(
      '[ArcGIS] registerArcGisOAuthConfig: failed to register ArcGIS OAuth config',
      error,
      meta
    );
    return null;
  }
}

export async function registerArcGisOAuthInfo(group: Group): Promise<boolean> {
  const portalUrl = registerArcGisOAuthConfig(group);
  if (!portalUrl) {
    return false;
  }

  try {
    const portalUser = await getPortalUser(portalUrl);
    console.log(
      '[ArcGIS] registerArcGisOAuthInfo: ArcGIS OAuth flow completed',
      {
        portalUrl,
        portalUser,
        group,
      }
    );
    return true;
  } catch (error) {
    const meta = {
      groupGuid: group.group_guid,
      groupName: group.group_name,
      portalUrl,
      clientId: group.oauth_client_id,
    };

    console.error(
      '[ArcGIS] registerArcGisOAuthInfo: getPortalUser() threw — ArcGIS OAuth flow failed',
      error,
      meta
    );

    Sentry.withScope((scope) => {
      // Tags
      scope.setTag('component', 'arcgis-oauth');
      scope.setTag('group_id', group.group_guid);
      // Metadata
      scope.setContext('arcgis_config', {
        groupName: group.group_name,
        portalUrl,
        clientId: group.oauth_client_id,
      });
      // Group error logs by fingerprint
      Sentry.captureException(error, {
        fingerprint: ['arcgis-oauth-error', portalUrl],
      });
    });

    throw error;
  }
}

/**
 * Checks if an existing OAuth session is valid.
 *
 * Assumes `registerArcGisOAuthConfig()` has already been called so that
 * IdentityManager has the matching ServerInfo + OAuthInfo registered.
 */
export async function isArcGisOAuthSessionValid(
  group: Group
): Promise<boolean> {
  const portalUrl = getPortalUrl(group);
  const signInUrl = `${portalUrl}/sharing/rest`;
  const meta: Record<string, unknown> = {
    portalUrl,
    signInUrl,
    groupGuid: group.group_guid,
    groupName: group.group_name,
    clientId: group.oauth_client_id,
    urlHash: typeof window !== 'undefined' ? window.location.hash : undefined,
  };

  if (!portalUrl || !group.oauth_client_id) {
    console.log(
      '[ArcGIS] isArcGisOAuthSessionValid: missing portalUrl or oauth_client_id on group; cannot check ArcGIS session.',
      meta
    );
    return false;
  }

  // checkSignInStatus is non-interactive and will not trigger a login popup.
  try {
    const credential = await IdentityManager.checkSignInStatus(signInUrl);
    console.log('[ArcGIS] isArcGisOAuthSessionValid: ArcGIS session is valid', {
      ...meta,
      credentialUserId: credential?.userId,
      credentialServer: credential?.server,
      credentialExpires: credential?.expires,
    });
    return true;
  } catch (error) {
    console.log(
      '[ArcGIS] isArcGisOAuthSessionValid: ArcGIS session is not valid',
      error,
      meta
    );
    return false;
  }
}

async function getPortalUser(portalUrl: string) {
  const portal = oauthPortal({
    url: portalUrl,
    authMode: 'immediate',
  });
  await portal.load();
  if (!portal.user) {
    throw new Error('No portal.user after load() — user is not signed in.');
  }

  return { portal, user: portal.user };
}

export type ScopedPortalItem = __esri.PortalItem & {
  userScoped?: boolean;
  authType?: ExternalLayerAccessType;
};

function createUserScopedPortalItems(
  items: __esri.PortalItem[]
): ScopedPortalItem[] {
  const scopedItems = Array.from(items);
  for (const item of scopedItems) {
    (item as ScopedPortalItem).userScoped = true;
    (item as ScopedPortalItem).authType = 'userAuth';
  }

  return scopedItems;
}

function createOauthScopedPortalItems(
  items: __esri.PortalItem[]
): ScopedPortalItem[] {
  const scopedItems = Array.from(items);
  for (const item of scopedItems) {
    (item as ScopedPortalItem).authType = 'userAuth';
    (item as ScopedPortalItem).userScoped = false;
  }

  return scopedItems;
}

const allowedTypes = [
  'Web Map',
  'Feature Service',
  'Feature Layer',
  'Map Service',
  'Vector Tile Service',
  'Image Service',
  'Scene Service',
  'KML',
  'WMS',
];
function filterItems(items: ScopedPortalItem[], search: string) {
  return items.filter((item) => {
    const hasSearch =
      item.title?.toLowerCase().includes(search) ||
      item.type?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search);

    return (
      allowedTypes.includes(item.type ?? '') &&
      hasSearch &&
      !item.tags?.includes('dhs')
    );
  });
}

function getRemainingPageIndexes(totalItems: number) {
  const pagesNeeded = Math.max(
    0,
    Math.ceil((totalItems - PAGE_SIZE) / PAGE_SIZE)
  );

  return Array.from({ length: pagesNeeded }, (_, i) => i + 1);
}

async function fetchAllPages<TPage, TItem>({
  fetchFirstPage,
  fetchPage,
  getItems,
  getTotal,
  getNextStart,
}: {
  fetchFirstPage: () => Promise<TPage>;
  fetchPage: (page: number) => Promise<TPage>;
  getItems: (page: TPage) => TItem[] | nullish;
  getTotal: (page: TPage) => number;
  getNextStart: (page: TPage) => number | nullish;
}) {
  const firstPage = await fetchFirstPage();
  const initialItems = getItems(firstPage) ?? [];
  if (getNextStart(firstPage) === -1) {
    return initialItems;
  }

  const remainingRequests = getRemainingPageIndexes(getTotal(firstPage)).map(
    (page) => fetchPage(page)
  );
  const remainingPages = await Promise.all(remainingRequests);
  const paginatedItems = remainingPages.flatMap((page) => getItems(page) ?? []);

  return initialItems.concat(paginatedItems);
}

async function fetchAllUserItems({
  user,
  portalState,
}: {
  user: __esri.PortalUser;
  portalState: PortalState;
}): Promise<ScopedPortalItem[]> {
  const search = portalState.searchTerm?.toLowerCase() ?? '';
  const allItems = await fetchAllPages({
    fetchFirstPage: () =>
      user.fetchItems({
        start: 1,
        num: PAGE_SIZE,
        includeSubfolderItems: true,
      }),
    fetchPage: (page) =>
      user.fetchItems({
        start: page * PAGE_SIZE,
        num: PAGE_SIZE,
        includeSubfolderItems: true,
      }),
    getItems: (page) => page.items,
    getTotal: (page) => page.total,
    getNextStart: (page) => page.nextStart,
  });

  return createUserScopedPortalItems(filterItems(allItems, search));
}

async function fetchAllPortalQueryItems({
  portal,
  params,
}: {
  portal: __esri.Portal;
  params: __esri.PortalQueryParamsProperties;
}) {
  const result = await fetchAllPages({
    fetchFirstPage: () =>
      portal.queryItems({
        ...params,
        num: PAGE_SIZE,
      }),
    fetchPage: (page) =>
      portal.queryItems({
        ...params,
        num: PAGE_SIZE,
        start: page * PAGE_SIZE,
      }),
    getItems: (page) => page.results,
    getTotal: (page) => page.total,
    getNextStart: (page) => page.nextQueryParams?.start,
  });

  return createOauthScopedPortalItems(result);
}

async function fetchAllFavoriteItems({
  user,
  portalState,
}: {
  user: __esri.PortalUser;
  portalState: PortalState;
}) {
  const search = portalState.searchTerm
    ? `(${portalState.searchTerm.trim()})`
    : '';
  const createFavoriteQueryParams = (start?: number) =>
    new PortalQueryParams({
      query: `${search} (${drawableTypes}) AND ${excludeDhsLayers}`,
      sortField: portalState.sort,
      sortOrder: 'desc',
      num: PAGE_SIZE,
      ...(start !== undefined ? { start } : {}),
    });

  const result = await fetchAllPages({
    fetchFirstPage: () => user.queryFavorites(createFavoriteQueryParams()),
    fetchPage: (page) =>
      user.queryFavorites(createFavoriteQueryParams(page * PAGE_SIZE)),
    getItems: (page) => page.results,
    getTotal: (page) => page.total,
    getNextStart: (page) => page.nextQueryParams?.start,
  });

  return createOauthScopedPortalItems(result);
}

async function portalUserItems({
  portalState,
  group,
}: {
  portalState: PortalState;
  group: Group;
}) {
  const portalUrl = getPortalUrl(group);

  try {
    const { portal, user } = await getPortalUser(portalUrl);
    const [userItems, favoriteItems, groups] = await Promise.all([
      fetchAllUserItems({ user, portalState }),
      fetchAllFavoriteItems({ user, portalState }),
      user.fetchGroups(),
    ]);

    const search = portalState.searchTerm
      ? `(${portalState.searchTerm.trim()})`
      : '';
    const groupIds = groups
      .map((g) => g.id)
      .filter((g): g is string => typeof g === 'string');
    const groupClause = groupIds.length
      ? `(${groupIds.map((id) => `group:${id}`).join(' OR ')})`
      : '';
    const [groupItems, orgItems] = await Promise.all([
      groupClause
        ? fetchAllPortalQueryItems({
            portal,
            params: {
              query: `${search} ${groupClause} AND (${drawableTypes}) AND ${excludeDhsLayers}`,
              sortField: portalState.sort,
              sortOrder: 'desc',
            },
          })
        : Promise.resolve([]),
      fetchAllPortalQueryItems({
        portal,
        params: {
          query: `${search} orgid:${user.orgId} AND (${drawableTypes}) AND ${excludeDhsLayers}`,
          sortField: portalState.sort,
          sortOrder: 'desc',
        },
      }),
    ]);
    const itemsById = new Map<string, ScopedPortalItem>();

    for (const item of userItems) {
      if (item.id) {
        itemsById.set(item.id, item);
      }
    }

    for (const item of favoriteItems) {
      if (item.id && !itemsById.has(item.id)) {
        itemsById.set(item.id, item);
      }
    }

    for (const item of groupItems) {
      if (item.id && !itemsById.has(item.id)) {
        itemsById.set(item.id, item);
      }
    }

    for (const item of orgItems) {
      if (item.id && !itemsById.has(item.id)) {
        itemsById.set(item.id, item);
      }
    }

    const items = Array.from(itemsById.values());

    return {
      groups: groups ?? [],
      userItems,
      favoriteItems,
      groupItems,
      orgItems,
      items,
    };
  } catch (error) {
    console.error('portalUserItems() failed', error);

    Sentry.withScope((scope) => {
      // Tags
      scope.setTag('component', 'arcgis-portal-items');
      scope.setTag('group_id', group.group_guid);
      // Metadata
      scope.setContext('query_params', {
        searchTerm: portalState.searchTerm,
        sort: portalState.sort,
      });
      // Group error logs by fingerprint
      Sentry.captureException(error, {
        fingerprint: ['arcgis-portal-items-error', portalUrl],
      });
    });

    // Re-throw so TanStack Query enters 'error' state
    throw error;
  }
}

export const usePortalUserItems = ({
  portalState,
  group,
}: {
  portalState: PortalState;
  group: Group;
}) =>
  useQuery({
    queryKey: [
      'copPortalUserItems',
      group.group_guid,
      portalState.searchTerm,
      portalState.sort,
    ],
    queryFn: () => portalUserItems({ portalState, group }),
  });
